import React from 'react';
import { motion } from 'framer-motion';
import { NoirBackground } from '../components/GameUI';
import { ArrowLeft, User, VenetianMask, Ghost } from 'lucide-react';

const HowToPlay = ({ onBack }) => (
  <div className="min-h-dvh flex flex-col items-center p-6 relative select-none">
    <NoirBackground />
    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md space-y-6 relative z-10 py-8">
      <button onClick={onBack} data-testid="howtoplay-back-btn"
        className="flex items-center gap-2 font-mono text-[#5A5A5A] text-xs uppercase tracking-wider hover:text-[#EF4444] transition-colors">
        <ArrowLeft size={14} /> Back
      </button>
      <div>
        <h1 className="font-typewriter text-4xl text-[#E0DDD5]">How to Play</h1>
        <div className="red-line w-full mt-3" />
      </div>
      <div className="space-y-6 text-sm text-[#888] font-mono leading-relaxed">

        <div>
          <h2 className="text-[#E0DDD5] font-bold uppercase tracking-wider text-xs mb-2">What is IMPOSTER?</h2>
          <p>IMPOSTER is a social deduction party game played on a single device. Players pass the phone around to secretly view their assigned word, then try to figure out who among them is the imposter — all through clever conversation and one-word clues.</p>
        </div>

        <div>
          <h2 className="text-[#E0DDD5] font-bold uppercase tracking-wider text-xs mb-2">The Roles</h2>
          <div className="space-y-3 mt-3">
            <div className="bg-gradient-to-b from-[#161616] to-[#111] border border-[#222] rounded-lg p-4 flex items-start gap-3">
              <User size={18} className="text-[#E0DDD5] mt-0.5 shrink-0" />
              <div>
                <div className="text-[#E0DDD5] font-bold text-sm">Civilian</div>
                <p className="text-[#666] text-xs mt-1">You know the secret word. Your job is to give subtle clues that prove you know it — without giving it away to the Imposter. Work together with other Civilians to identify and vote out the suspicious players.</p>
              </div>
            </div>
            <div className="bg-gradient-to-b from-[#161616] to-[#111] border border-[#EF4444]/20 rounded-lg p-4 flex items-start gap-3">
              <VenetianMask size={18} className="text-[#EF4444] mt-0.5 shrink-0" />
              <div>
                <div className="text-[#EF4444] font-bold text-sm">Imposter</div>
                <p className="text-[#666] text-xs mt-1">You receive a different word that is related to the Civilian word but not the same. You must bluff your way through the round — give clues that sound convincing enough so no one suspects you. If you survive long enough to outnumber the Civilians, you win!</p>
              </div>
            </div>
            <div className="bg-gradient-to-b from-[#161616] to-[#111] border border-white/10 rounded-lg p-4 flex items-start gap-3">
              <Ghost size={18} className="text-white mt-0.5 shrink-0" />
              <div>
                <div className="text-white font-bold text-sm">Mr. White</div>
                <p className="text-[#666] text-xs mt-1">You have no word at all — you are completely in the dark. Listen carefully to what others say, pick up on patterns, and try to blend in. If you get voted out, you get one final chance: guess the Civilian word correctly and you win instantly!</p>
              </div>
            </div>
          </div>
        </div>

        <div>
          <h2 className="text-[#E0DDD5] font-bold uppercase tracking-wider text-xs mb-2">Game Setup</h2>
          <ol className="list-decimal list-inside space-y-2 ml-1">
            <li>Choose how many <span className="text-[#E0DDD5]">Civilians</span>, <span className="text-[#EF4444]">Imposters</span>, and <span className="text-white">Mr. Whites</span> will play. You need at least 3 players.</li>
            <li>Each player enters their name one by one.</li>
            <li>The game randomly assigns roles and words to each player.</li>
          </ol>
        </div>

        <div>
          <h2 className="text-[#E0DDD5] font-bold uppercase tracking-wider text-xs mb-2">How a Round Works</h2>
          <ol className="list-decimal list-inside space-y-2 ml-1">
            <li><span className="text-[#E0DDD5] font-bold">Secret Word Reveal</span> — Pass the phone to each player. They tap to privately see their word (or role, in Mr. White's case), then pass it to the next person. No peeking!</li>
            <li><span className="text-[#E0DDD5] font-bold">Clue Phase</span> — The game picks a random player to start (Mr. White will never be first). Going around the group, each player says ONE word as a clue. The clue should hint that you know the word — but be vague enough that the Imposter can't figure it out.</li>
            <li><span className="text-[#E0DDD5] font-bold">Discussion</span> — After everyone has given their clue, discuss openly. Who gave a suspicious clue? Who was too vague? Who seemed to be guessing?</li>
            <li><span className="text-[#E0DDD5] font-bold">Voting</span> — Each player votes for who they think is the Imposter (or Mr. White). The player with the most votes is eliminated.</li>
            <li><span className="text-[#E0DDD5] font-bold">Elimination</span> — The eliminated player's role is revealed to everyone.</li>
          </ol>
        </div>

        <div>
          <h2 className="text-[#E0DDD5] font-bold uppercase tracking-wider text-xs mb-2">Mr. White's Last Chance</h2>
          <p>If Mr. White is voted out, they get one final opportunity — guess the Civilian word. If they guess correctly, <span className="text-white font-bold">Mr. White wins the entire game</span> regardless of everything else. If they guess wrong, the game continues without them.</p>
        </div>

        <div>
          <h2 className="text-[#E0DDD5] font-bold uppercase tracking-wider text-xs mb-2">How to Win</h2>
          <div className="space-y-2 ml-1">
            <p><span className="text-[#E0DDD5] font-bold">Civilians win</span> — when all Imposters and Mr. Whites are eliminated.</p>
            <p><span className="text-[#EF4444] font-bold">Imposters win</span> — when they equal or outnumber the Civilians (with no Mr. White remaining).</p>
            <p><span className="text-white font-bold">Mr. White wins</span> — by correctly guessing the Civilian word when eliminated, or by being the last one standing.</p>
          </div>
        </div>

        <div>
          <h2 className="text-[#E0DDD5] font-bold uppercase tracking-wider text-xs mb-2">Tips & Strategy</h2>
          <ul className="list-disc list-inside space-y-2 ml-1">
            <li><span className="text-[#E0DDD5]">Civilians:</span> Don't make your clue too obvious or the Imposter will catch on. Don't make it too vague either, or you'll look suspicious.</li>
            <li><span className="text-[#EF4444]">Imposters:</span> Listen carefully to other clues before giving yours. Try to give a clue that fits the theme without knowing the exact word.</li>
            <li><span className="text-white">Mr. White:</span> Stay calm. Copy the energy of others. Give a generic clue that could apply to many words. Your best weapon is confidence.</li>
            <li>Pay attention to who hesitates, who gives their clue too quickly, and who seems to be copying others.</li>
            <li>The best clues are specific enough to prove knowledge but vague enough to not hand it to the Imposter.</li>
          </ul>
        </div>

        <div>
          <h2 className="text-[#E0DDD5] font-bold uppercase tracking-wider text-xs mb-2">Example Round</h2>
          <div className="bg-gradient-to-b from-[#161616] to-[#111] border border-[#222] rounded-lg p-4 space-y-2 text-xs">
            <p><span className="text-[#5A5A5A]">Civilian word:</span> <span className="text-[#E0DDD5] font-bold">Pizza</span></p>
            <p><span className="text-[#5A5A5A]">Imposter word:</span> <span className="text-[#EF4444] font-bold">Biryani</span></p>
            <div className="red-line w-8 my-2" />
            <p><span className="text-[#E0DDD5]">Player A (Civilian):</span> "Cheese" — good clue, specific to pizza</p>
            <p><span className="text-[#EF4444]">Player B (Imposter):</span> "Spicy" — works for biryani but also sounds food-related</p>
            <p><span className="text-[#E0DDD5]">Player C (Civilian):</span> "Slice" — strong clue pointing to pizza</p>
            <p><span className="text-white">Player D (Mr. White):</span> "Delicious" — safe generic food clue</p>
            <div className="red-line w-8 my-2" />
            <p className="text-[#5A5A5A]">Now the discussion begins — Player B said "Spicy" which doesn't quite fit pizza. Suspicious? Or just a bad clue from a Civilian? That's the fun of IMPOSTER!</p>
          </div>
        </div>

      </div>
    </motion.div>
  </div>
);

export default HowToPlay;
